import { useReactDndConstructs } from '../../../dnd/ReactDndContext';
import {
  DropTargetMonitor,
  ConnectDragSource,
  ConnectDropTarget,
  ConnectDragPreview,
} from 'react-dnd';

export interface DragDrop {
  dragRef: ConnectDragSource;
  dropRef: ConnectDropTarget;
  preview: ConnectDragPreview;
  isDragging: boolean;
  rowIndex: number;
  isOverCurrent?: boolean;
}

export function useRowDragAndDrop(props: {
  rowIndex: number;
  rowKey?: string;
  prefix?: string;
  begin?: (item?: any) => void;
  end?: () => void;
  hover?: (item: any, monitor: DropTargetMonitor) => void;
  drop?: (item: any, monitor: DropTargetMonitor) => void;
  isDraggable?: (index: number) => boolean;
}): DragDrop {
  const index = props.rowIndex;

  const source = {
    item: () => {
      // NOTE: begin() has been deprecated in react-dnd(v14.0.0)
      // replaced by functional version of item in react-dnd(v14.0.0)
      // @see - https://github.com/react-dnd/react-dnd/releases/tag/v14.0.0
      // @see - https://react-dnd.github.io/react-dnd/docs/api/use-drag
      props.begin?.(props.prefix);
      return { type: 'ROW', id: index, index, dndPrefix: props.prefix };
    },
    options: { dropEffect: 'copy' },
    collect: (mon: { isDragging: () => any }) => ({
      isDragging: !!mon.isDragging(),

      rowIndex: index,
    }),
    end(_monitor: any): void {
      props.end?.();
    },
    canDrag(_monitor: any): boolean {
      return props.isDraggable ? props.isDraggable(index) : true;
    },
  };

  const target = {
    accept: 'ROW',
    drop(item: any, monitor: DropTargetMonitor) {
      props.drop?.(item, monitor);
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver,
      isOverCurrent: monitor.isOver({ shallow: true }),
      item: monitor.getItem(),
    }),

    hover(item: any, monitor: DropTargetMonitor) {
      if (!dropRef) {
        return;
      }
      props.hover?.(item, monitor);
    },
  };
  const { useDrag, useDrop } = useReactDndConstructs();
  const [{ isDragging, rowIndex }, dragRef, preview] = useDrag({
    // NOTE: type is now a required prop and is decoupled from item in react-dnd(v14.0.0)
    // @see - https://github.com/react-dnd/react-dnd/releases/tag/v14.0.0
    type: 'ROW',
    ...source,
  });
  const [{ isOverCurrent }, dropRef] = useDrop(target);

  return {
    dragRef,
    dropRef,
    isDragging,
    rowIndex,
    preview,
    isOverCurrent,
  };
}
